import React, { useState } from 'react';
import logoPlick from '@assets/images/logo.svg';
import DefaultImgBussiness from '@assets/images/DefaultImgBussiness.png';
import { V4 } from './tokens';
import BranchPicker from './BranchPicker';
import useModules from '@utils/hooks/useModules';
import { ModuleName } from '@utils/constants';
import type { ModuleKey } from '@interfaces/Permissions';

interface DrawerItemDef {
  id: string;
  label: string;
  branchDep?: boolean;
  danger?: boolean;
  icon?: React.ReactNode;
  // Gate opcional por módulo: si está, el item solo se muestra si el comercio tiene ese módulo
  // (liberación controlada). Los items SIN `module` se muestran siempre (comportamiento actual;
  // las páginas ya se auto-gatean vía redirect de PageWrapper).
  module?: ModuleKey;
}

interface MobileNavDrawerV4Props {
  open: boolean;
  onClose: () => void;
  activeBranch: string;
  branches: string[];
  isAccountLevel: boolean;
  // Multisucursal: el switch se ve siempre (todos), pero solo cambia si canChangeBranch
  // (root + módulo + ≥2). showCreateBranch = root → ve el CTA "Crear sucursal" (WhatsApp).
  canChangeBranch: boolean;
  showCreateBranch: boolean;
  onBranchSelect: (branch: string) => void;
  activePathname: string;
  onNavigate: (path: string) => void;
  onGoToMenu: () => void;
  onLogout: () => void;
  commerceName: string;
  userName: string;
  commerceLogotipo?: string;
}

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      padding: '14px 16px 8px',
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '.08em',
      color: V4.secondary,
      borderTop: `1px solid ${V4.grayLight}`,
      marginTop: 4,
      background: V4.grayBlueLight,
    }}
  >
    {children}
  </div>
);

interface DrawerItemProps {
  item: DrawerItemDef;
  active: boolean;
  withIcon?: boolean;
  onClick: () => void;
}

const DrawerItem: React.FC<DrawerItemProps> = ({ item, active, withIcon, onClick }) => (
  <button
    onClick={onClick}
    style={{
      width: '100%',
      textAlign: 'left',
      padding: '11px 16px',
      border: 0,
      fontFamily: 'inherit',
      background: active ? V4.secondary : 'transparent',
      color: active ? V4.white : item.danger ? V4.danger : V4.primary,
      fontSize: 14,
      fontWeight: active ? 600 : 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 10,
    }}
  >
    {withIcon && item.icon && (
      <span style={{ color: item.danger ? V4.danger : V4.grayUltraDark, display: 'inline-flex' }}>{item.icon}</span>
    )}
    <span style={{ flex: 1 }}>{item.label}</span>
    {item.branchDep && (
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: active ? 'rgba(255,255,255,.7)' : V4.secondary,
          flexShrink: 0,
        }}
      />
    )}
  </button>
);

const NAV_ITEMS: DrawerItemDef[] = [
  { id: 'control-pedidos', label: 'Control de pedidos', branchDep: true },
  { id: 'estadisticas', label: 'Estadísticas', branchDep: true },
  // Mis clientes — gate ESTRICTO de la superficie LISTADO (feature de paga controlada, sin
  // fallback). Junto a Estadísticas para agrupar las vistas de análisis. Ruta '/clientes'.
  { id: 'clientes', label: 'Mis clientes', branchDep: true, module: ModuleName.PERFIL_CLIENTE_LISTADO },
  // Conversaciones de WhatsApp — SIN gate de módulo a propósito (paridad con la sidebar de
  // escritorio): el add-on es independiente del plan, así que la entrada se ofrece a todos y
  // /conversaciones redirige sola a la página de descripción cuando falta el add-on.
  // Feature nueva ⇒ vive en el drawer, nunca en el bottom nav.
  { id: 'conversaciones', label: 'Conversaciones', branchDep: true },
  { id: 'menu', label: 'Menú', branchDep: true },
  { id: 'cupones', label: 'Cupones', branchDep: true },
  { id: 'mesas', label: 'Mis Mesas', branchDep: true },
  // Pantallas (plick-screens) — sin gate de módulo a propósito: la página se
  // auto-gatea con preview + oferta (patrón upsell, no ocultar).
  { id: 'pantallas', label: 'Pantallas', branchDep: true },
  { id: 'anuncios', label: 'Crear anuncios', branchDep: true },
  { id: 'compartir-menu', label: 'Compartir menú', branchDep: true },
  { id: 'perfil-social', label: 'Perfil Social', branchDep: false },
];

const SETTINGS_ITEMS: DrawerItemDef[] = [
  { id: 'datos-del-comercio', label: 'Datos del comercio' },
  { id: 'horarios', label: 'Horarios' },
  { id: 'tarifas', label: 'Tarifas y entregas' },
  { id: 'usuarios', label: 'Alta de usuarios' },
  { id: 'ajustes', label: 'Ajustes' },
  { id: 'integraciones', label: 'Integraciones' },
];

export const MobileNavDrawerV4: React.FC<MobileNavDrawerV4Props> = ({
  open,
  onClose,
  activeBranch,
  branches,
  isAccountLevel,
  canChangeBranch,
  showCreateBranch,
  onBranchSelect,
  activePathname,
  onNavigate,
  onGoToMenu,
  onLogout,
  commerceName,
  userName,
  commerceLogotipo,
}) => {
  const [branchPickerOpen, setBranchPickerOpen] = useState(false);
  const { hasModule } = useModules();
  // Items visibles = los sin gate + los cuyo módulo tiene el comercio (p. ej. Mis clientes).
  const visibleNavItems = NAV_ITEMS.filter((it) => !it.module || hasModule(it.module));
  // El switch queda bloqueado (visible pero sin cambiar) si la sección es account-level
  // o si la cuenta no puede cambiar de sucursal (no root / sin módulo / 1 sucursal).
  const branchLocked = isAccountLevel || !canChangeBranch;
  const CREAR_SUCURSAL_WA =
    'https://api.whatsapp.com/send?phone=5219935318237&text=Hola%2C%20quiero%20crear%20una%20nueva%20sucursal%20para%20mi%20comercio.';

  const accountItems: DrawerItemDef[] = [
    {
      id: 'mi-menu',
      label: 'Ir a mi menú',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M21 12a9 9 0 1 1-9-9M21 3l-9 9M21 3v6h-6"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: 'mi-cuenta',
      label: 'Mi cuenta',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2" />
          <path
            d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
        </svg>
      ),
    },
    {
      id: 'sugerencias',
      label: 'Sugerencias',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.2 1 2v.3h6V17c0-.8.4-1.5 1-2A7 7 0 0 0 12 2z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: 'ayuda',
      label: 'Centro de Ayuda',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
          <path
            d="M9.5 9.5a2.5 2.5 0 1 1 3.8 2.1c-.8.5-1.3 1-1.3 2"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="17" r="1" fill="currentColor" />
        </svg>
      ),
    },
    {
      id: 'soporte',
      label: 'Soporte',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3c1.4.8 3 1.3 4.8 1.3 5.5 0 10-4.5 10-10S17.5 2 12 2z" />
        </svg>
      ),
    },
    {
      id: 'actualizaciones',
      label: 'Actualizaciones',
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M3 12a9 9 0 0 1 15-6.7L21 8M21 3v5h-5M21 12a9 9 0 0 1-15 6.7L3 16M3 21v-5h5"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
    {
      id: 'logout',
      label: 'Cerrar Sesión',
      danger: true,
      icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path
            d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
    },
  ];

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.5)' }} />
      <aside
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          bottom: 0,
          width: 'min(300px, 86%)',
          background: V4.white,
          animation: 'plick-drawer-in 220ms cubic-bezier(.2,.8,.2,1)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'visible',
        }}
      >
        {/* Header */}
        <div
          style={{
            background: V4.primary,
            color: V4.white,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={logoPlick} style={{ height: 22 }} alt="Plick" />
            <div style={{ fontSize: 12, color: V4.white, opacity: 0.85 }}>
              Pedidos a un{' '}
              <span
                style={{
                  background: V4.secondaryLight,
                  color: V4.primaryDark,
                  fontWeight: 700,
                  padding: '0 6px',
                  borderRadius: 3,
                }}
              >
                click.
              </span>
            </div>
            <button
              onClick={onClose}
              style={{
                marginLeft: 'auto',
                background: 'transparent',
                border: 0,
                color: V4.white,
                cursor: 'pointer',
                padding: 4,
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* User info */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                border: `2px solid ${V4.white}`,
                background: V4.white,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                overflow: 'hidden',
              }}
            >
              <img
                src={commerceLogotipo || DefaultImgBussiness}
                alt="Logo del comercio"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: V4.white,
                  lineHeight: 1.15,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {commerceName}
              </div>
              <div style={{ fontSize: 12, color: V4.white, opacity: 0.7 }}>{userName}</div>
            </div>
          </div>

          {/* Branch selector — visible siempre; bloqueado si no se puede cambiar */}
          <button
            onClick={() => !branchLocked && setBranchPickerOpen((o) => !o)}
            disabled={branchLocked}
            title={
              isAccountLevel
                ? 'Esta sección no depende de sucursal'
                : canChangeBranch
                  ? 'Cambiar sucursal'
                  : 'El cambio de sucursal requiere el módulo multisucursal'
            }
            style={{
              width: '100%',
              background: 'rgba(255,255,255,.12)',
              border: '1px solid rgba(255,255,255,.18)',
              borderRadius: 8,
              padding: '8px 10px',
              cursor: branchLocked ? 'default' : 'pointer',
              fontFamily: 'inherit',
              color: V4.white,
              opacity: branchLocked ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              textAlign: 'left',
            }}
          >
            <span
              style={{
                width: 26,
                height: 26,
                borderRadius: 6,
                background: V4.secondary,
                color: V4.white,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 22s8-5 8-12a8 8 0 1 0-16 0c0 7 8 12 8 12z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span style={{ flex: 1, minWidth: 0, lineHeight: 1.15 }}>
              <span
                style={{
                  display: 'block',
                  fontSize: 10,
                  opacity: 0.75,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                {isAccountLevel ? 'Aplica a tu cuenta' : 'Sucursal activa'}
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 13,
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {isAccountLevel ? 'Toda la cuenta' : activeBranch}
              </span>
            </span>
            {!branchLocked ? (
              <span
                style={{
                  flexShrink: 0,
                  transform: branchPickerOpen ? 'rotate(180deg)' : 'none',
                  transition: 'transform 200ms',
                  display: 'inline-flex',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 9l6 6 6-6"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            ) : (
              !isAccountLevel && (
                <span style={{ flexShrink: 0, display: 'inline-flex', opacity: 0.8 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                    <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
                    <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
              )
            )}
          </button>

          {branchPickerOpen && !branchLocked && (
            <div
              style={{
                background: 'rgba(255,255,255,.08)',
                borderRadius: 8,
                padding: 8,
                marginTop: -2,
              }}
            >
              <BranchPicker
                branches={branches.map((nombre) => ({ nombre }))}
                current={activeBranch}
                variant="dark"
                autoFocus
                maxHeight={220}
                onPick={(n) => {
                  onBranchSelect(n);
                  setBranchPickerOpen(false);
                }}
              />
            </div>
          )}

          {/* Crear sucursal — CTA de ventas (WhatsApp), visible siempre para root */}
          {showCreateBranch && (
            <a
              href={CREAR_SUCURSAL_WA}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onClose}
              style={{
                marginTop: 8,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 10px',
                borderRadius: 8,
                background: 'rgba(255,255,255,.08)',
                border: '1px solid rgba(255,255,255,.18)',
                color: V4.white,
                fontFamily: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                textDecoration: 'none',
                boxSizing: 'border-box',
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  background: 'rgba(255,255,255,.12)',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </span>
              Crear sucursal
            </a>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          <SectionHeader>Operación</SectionHeader>
          {visibleNavItems.map((it) => (
            <DrawerItem
              key={it.id}
              item={it}
              active={activePathname.includes(it.id)}
              onClick={() => onNavigate(it.id)}
            />
          ))}

          <SectionHeader>Ajustes</SectionHeader>
          {SETTINGS_ITEMS.map((it) => (
            <DrawerItem
              key={it.id}
              item={it}
              active={activePathname.includes(it.id)}
              onClick={() => onNavigate(it.id)}
            />
          ))}

          <SectionHeader>Cuenta y ayuda</SectionHeader>
          {accountItems.map((it) => (
            <DrawerItem
              key={it.id}
              item={it}
              active={false}
              withIcon
              onClick={() => {
                if (it.id === 'mi-menu') onGoToMenu();
                else if (it.id === 'logout') onLogout();
                else onNavigate(it.id);
              }}
            />
          ))}
        </nav>
      </aside>
      {/* keyframe `plick-drawer-in` se carga vía tokens.css a module-load time
          (no inline) para evitar race condition entre parser y animation engine. */}
    </div>
  );
};
